import { createContext, ReactNode, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { FeatureFlag, useFeatureToggle } from 'modules/unleash';
import { useUserUUID } from 'utils/auth';
import { env } from '../../../src/constants';

const loadBraze = async () => {
  console.log('Loading Braze');
  return await import('@braze/web-sdk');
};

export type BrazeInstanceType = typeof import('@braze/web-sdk');
export type BrazeContextType = BrazeInstanceType | null | undefined;

export const BrazeContext = createContext<BrazeContextType>(undefined);

interface BrazeProviderProps {
  children: ReactNode;
}

export const BrazeProvider = ({ children }: BrazeProviderProps) => {
  const [brazeInstance, setBrazeInstance] = useState<BrazeContextType>(null);
  const uuid = useUserUUID();
  const { isLoading } = useAuth0();

  const { isEnabled: isPushEnabled, isLoading: isFeatureToggleLoading } =
    useFeatureToggle(FeatureFlag.PRO_TALENT_IS_PUSH_ENABLED);

  useEffect(() => {
    const canInit = shouldInitializeBraze(
      isFeatureToggleLoading,
      isLoading,
      uuid
    );
    console.log(
      `[Braze Debug] isFeatureToggleLoading: ${isFeatureToggleLoading}`
    );
    console.log(`[Braze Debug] isLoading: ${isLoading}`);
    console.log(`[Braze Debug] uuid: ${uuid}`);
    console.log(`[Braze Debug] Conditions met to initialize: ${canInit}`);

    if (canInit) {
      const initializeBrazeClient = async () => {
        try {
          console.log('[Braze Debug] Loading Braze SDK...');
          const braze = await loadBraze();
          console.log('[Braze Debug] Braze SDK loaded.');

          console.log('[Braze Debug] Initializing Braze...');
          braze.initialize(env.VITE_BRAZE_API_KEY, {
            baseUrl: env.VITE_BRAZE_ENDPOINT,
            manageServiceWorkerExternally: true,
            noCookies: true
          });
          console.log('[Braze Debug] Braze initialized.');

          console.log('[Braze Debug] Registering service worker...');
          if ('serviceWorker' in navigator) {
            try {
              const registration =
                await navigator.serviceWorker.register('/service-worker.js');
              console.log(
                '[Braze Debug] Service Worker registered successfully with scope:',
                registration.scope
              );
            } catch (error) {
              console.error(
                '[Braze Debug] Service Worker registration failed:',
                error
              );
            }
          } else {
            console.warn(
              '[Braze Debug] Service workers are not supported in this browser.'
            );
          }

          if (isPushEnabled && uuid) {
            console.log(`[Braze Debug] Changing user to UUID: ${uuid}`);
            braze.changeUser(uuid);
            console.log('[Braze Debug] User changed.');
          } else {
            console.log(
              `[Braze Debug] Skipping changeUser. isPushEnabled: ${isPushEnabled}, uuid: ${uuid}`
            );
          }

          console.log('[Braze Debug] Opening session...');
          braze.openSession();
          console.log('[Braze Debug] Session opened.');
          setBrazeInstance(braze);
        } catch (error) {
          console.error(
            '[Braze Debug] Failed during Braze client initialization:',
            error
          );
        }
      };

      void initializeBrazeClient();
    }
  }, [isLoading, isFeatureToggleLoading, uuid, isPushEnabled]);

  return (
    <BrazeContext.Provider value={brazeInstance}>
      {children}
    </BrazeContext.Provider>
  );
};

const shouldInitializeBraze = (
  isFeatureToggleLoading: boolean,
  isLoading: boolean,
  uuid: string | undefined
) => {
  return !isFeatureToggleLoading && !isLoading && !!uuid;
};
